import { createFileRoute } from "@tanstack/react-router";
import SeoLandingPage, {
  buildFaqSchema,
  buildLocalBusinessSchema,
} from "@/components/SeoLandingPage";
import heroImage from "@/assets/sprinter-exec-red.jpg?format=webp&quality=72&w=1400&imagetools";
import heroVideo from "@/assets/aviation/private-jet-hero.mp4";
import midImage from "@/assets/sprinter-exec-yellow-cabin.jpg?format=webp&quality=72&w=1400&imagetools";

const PAGE_URL = "https://atlantaluxurysprinters.com/private-jet-airports-georgia";
const PAGE_TITLE = "Georgia Private Jet Airports | VIP Chauffeur & FBO Transportation";
const PAGE_DESC =
  "Private jet ground transportation at every private aviation airport in Georgia. Discreet VIP, celebrity, and executive chauffeur service in Mercedes-Benz Sprinter Limousines at ATL, PDK, FTY, RYY, LZU, SAV, AGS, AHN, MCN, CSG, BQK and beyond.";

const faqs = [
  {
    q: "Which private jet airports in Georgia do you serve?",
    a: "We provide chauffeur transportation at every private aviation airport in Georgia, DeKalb-Peachtree (PDK), Fulton County/Charlie Brown Field (FTY), Cobb County International/McCollum Field (RYY), Gwinnett County Briscoe Field (LZU), Cherokee County (CNI), Atlanta Regional Falcon Field (FFC), Newnan-Coweta (CCO), Cartersville (VPC), plus statewide fields including Savannah/Hilton Head (SAV), Augusta Regional (AGS) and Daniel Field (DNL), Athens Ben Epps (AHN), Macon Middle Georgia Regional (MCN), Columbus (CSG), Albany (ABY), Valdosta (VLD), Brunswick Golden Isles (BQK), St. Simons Island (SSI), Rome Russell Field (RMG), Gainesville Lee Gilmer (GVL), LaGrange-Callaway (LGC), Thomasville (TVI), and the FBO terminals at Hartsfield-Jackson (ATL).",
  },
  {
    q: "Do you provide discreet transportation for celebrities and public figures?",
    a: "Yes. We regularly handle entertainers, professional athletes, touring artists, and executive principals arriving privately in Georgia. Chauffeurs sign confidentiality agreements, vehicles carry no exterior branding, itineraries are shared only with assigned staff, and privacy glass keeps the cabin unseen. We coordinate with security details, tour managers, and advance teams.",
  },
  {
    q: "Can the chauffeur meet us planeside at the private aviation terminal?",
    a: "Where the FBO authorizes ramp access, including Signature Flight Support, Atlantic Aviation, Million Air, Epps Aviation, Fulton Jet Center, Hill Aircraft, and Sheltair, your Sprinter or SUV is staged planeside so passengers step from the airstairs directly into the vehicle without entering the lobby.",
  },
  {
    q: "Do you handle transfers between Georgia airports and out-of-state destinations?",
    a: "Yes. Long-distance chauffeur transfers from Georgia private terminals to Greenville, Charlotte, Birmingham, Chattanooga, Nashville, Charleston, and the Florida panhandle are routine, as are repositioning drives when weather diverts an aircraft to a different Georgia field.",
  },
  {
    q: "Can you support an entire touring party or executive team?",
    a: "Mercedes-Benz Sprinter Limousines seat up to 14 passengers with luggage, and we run multi-vehicle convoys with Suburban SUVs for principals, security, crew, and production gear moving together from the same ramp.",
  },
  {
    q: "How far in advance should private aviation transportation be booked?",
    a: "24–48 hours secures preferred vehicles, but our aviation desk operates around the clock and regularly confirms same-day and diverted-arrival coverage. Send the tail number, FBO, and ETA and we handle the rest.",
  },
  {
    q: "Do you track tail numbers and adjust for early or delayed arrivals?",
    a: "Every reservation is monitored by tail number. If your aircraft lands 40 minutes early or holds for weather, our team adjusts the chauffeur's stage time automatically, there is no waiting on the ramp and no call required from the crew.",
  },
  {
    q: "Are crew transfers and multi-leg itineraries available?",
    a: "Yes. We move flight crews to hotels, run repositioning legs between Georgia fields, and hold vehicles as-directed for principals working a multi-stop day across Atlanta, Savannah, or Augusta.",
  },
];

const routeTable = {
  title: "Georgia Private Airport Transfer Distances & Drive Times",
  intro:
    "Typical chauffeur drive times from Georgia's busiest private aviation fields to the destinations our clients request most. Times reflect real traffic patterns, not idealized estimates.",
  rows: [
    {
      route: "PDK (DeKalb-Peachtree) → Buckhead",
      distance: "9 miles",
      time: "18–30 min",
      notes: "Clairmont to GA-400 or Peachtree surface streets; avoid I-85 at rush hour.",
    },
    {
      route: "PDK → Mercedes-Benz Stadium / Downtown",
      distance: "17 miles",
      time: "28–50 min",
      notes: "Event-day loading dock and secure-entry coordination available.",
    },
    {
      route: "FTY (Charlie Brown Field) → Downtown Atlanta",
      distance: "10 miles",
      time: "18–30 min",
      notes: "Closest private field to the stadium, State Farm Arena, and the convention corridor.",
    },
    {
      route: "RYY (McCollum Field) → The Battery / Truist Park",
      distance: "12 miles",
      time: "18–28 min",
      notes: "Bells Ferry to I-575; the fastest private arrival for Cobb engagements.",
    },
    {
      route: "LZU (Briscoe Field) → Alpharetta / Johns Creek",
      distance: "18 miles",
      time: "25–40 min",
      notes: "Preferred field for North Fulton corporate campuses.",
    },
    {
      route: "ATL FBO (Signature) → Buckhead",
      distance: "16 miles",
      time: "25–45 min",
      notes: "Private terminal access off Loop Road, well away from commercial curbside.",
    },
    {
      route: "SAV (Savannah/Hilton Head) → Historic District",
      distance: "11 miles",
      time: "18–25 min",
      notes: "Sheltair and Signature ramps; Hilton Head extensions available.",
    },
    {
      route: "AGS (Augusta Regional) → Augusta National area",
      distance: "8 miles",
      time: "15–25 min",
      notes: "Tournament week requires early booking and credentialed routing.",
    },
    {
      route: "AHN (Athens Ben Epps) → UGA / Downtown Athens",
      distance: "5 miles",
      time: "10–15 min",
      notes: "Game-weekend and recruiting-visit transportation.",
    },
    {
      route: "BQK (Brunswick Golden Isles) → Sea Island / St. Simons",
      distance: "12 miles",
      time: "20–30 min",
      notes: "Resort and private-club drop-offs on the island.",
    },
  ],
  footnote:
    "Drive times are typical ranges for a chauffeured Sprinter, not guarantees. Our team pads every itinerary against live traffic and your actual wheels-down time.",
};

const detailSections = [
  {
    title: "Every Private Aviation Airport in Georgia, Covered",
    body: [
      "Private aviation in Georgia extends far past the Atlanta perimeter. Our chauffeur network covers the metro fields daily and repositions statewide for coastal, golf, collegiate, and corporate travel.",
    ],
    list: [
      "Hartsfield-Jackson Atlanta International (ATL), Signature Flight Support private terminal",
      "DeKalb-Peachtree Airport (PDK), Atlantic Aviation, Signature, Million Air, Epps Aviation",
      "Fulton County Airport / Charlie Brown Field (FTY), Fulton Jet Center, Hill Aircraft",
      "Cobb County International / McCollum Field (RYY), Atlantic Aviation",
      "Gwinnett County Briscoe Field (LZU)",
      "Cherokee County Regional (CNI), Canton",
      "Atlanta Regional Falcon Field (FFC), Peachtree City",
      "Newnan-Coweta County (CCO) and Cartersville (VPC)",
      "Savannah/Hilton Head International (SAV), Sheltair, Signature",
      "Augusta Regional (AGS) and Daniel Field (DNL)",
      "Athens Ben Epps (AHN) and Gainesville Lee Gilmer (GVL)",
      "Macon Middle Georgia Regional (MCN) and Milledgeville Baldwin County (MLJ)",
      "Columbus Airport (CSG) and LaGrange-Callaway (LGC)",
      "Albany Southwest Georgia Regional (ABY) and Valdosta Regional (VLD)",
      "Brunswick Golden Isles (BQK), St. Simons Island (SSI), Jekyll Island (09J)",
      "Rome Richard B. Russell (RMG), Dalton Municipal (DNN), Thomasville Regional (TVI)",
    ],
  },
  {
    title: "Discreet Transportation for Celebrities, Athletes & Public Figures",
    body: [
      "Atlanta is a production and touring hub, and private arrivals here are rarely anonymous. Our celebrity and VIP ground transportation protocol is built around not being noticed: unmarked vehicles, privacy glass, planeside staging so passengers never cross a public lobby, and chauffeurs who have signed confidentiality agreements before they receive an itinerary.",
      "We coordinate directly with security details, tour managers, studio transportation coordinators, and advance teams. Manifests stay internal, routes are pre-driven for venue and studio access, and multi-vehicle convoys keep principals, security, and crew moving as one unit from the ramp to the hotel, set, arena, or estate.",
    ],
    list: [
      "NDA-bound chauffeurs with entertainment and executive-protection experience",
      "Unbranded Mercedes-Benz Sprinters and Suburban SUVs with privacy glass",
      "Planeside pickup that avoids FBO lobbies and public terminals entirely",
      "Convoy operation for principal, security, and production teams",
      "Pre-driven venue, studio, and loading-dock access routes",
      "As-directed hourly hold for unpredictable production or event schedules",
    ],
  },
  {
    title: "How the Private Aviation Terminal Pickup Works",
    body: [
      "Send the tail number, FBO name, and estimated wheels-down time. Our team confirms coverage, assigns a chauffeur, and begins tracking the aircraft. Where the facility permits ramp access, your vehicle stages planeside before the airstairs deploy; where it does not, the chauffeur waits inside the FBO lounge and loads at the private curb, never at a commercial terminal.",
      "If the flight diverts to another Georgia field for weather, we reposition the vehicle rather than cancel. Crew transfers, second-leg pickups, and baggage-only runs can be added to the same reservation.",
    ],
  },
  {
    title: "Choosing the Right Vehicle for a Private Arrival",
    body: [
      "Aircraft size dictates party size, and party size dictates the vehicle. A light jet arrival of two or three passengers is best served by a Suburban SUV; a midsize or heavy jet carrying a full executive team or touring party belongs in a Sprinter Limousine.",
    ],
    list: [
      "Suburban SUV, 1–5 passengers, principals and small executive parties",
      "Executive Sprinter, 8–12 passengers, corporate teams and family travel",
      "Mercedes-Benz Sprinter Limousine, up to 14 passengers with full luggage",
      "Multi-vehicle convoy, touring parties, security details, and production crews",
    ],
  },
];

export const Route = createFileRoute("/private-jet-airports-georgia")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      {
        name: "keywords",
        content:
          "Georgia private jet airports, private aviation terminal Georgia, celebrity car service Atlanta, VIP chauffeur Georgia, private jet ground transportation Georgia, FBO car service Georgia, luxury airport transfer Savannah, Augusta private jet car service, executive chauffeur Georgia airports",
      },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildLocalBusinessSchema({
            url: PAGE_URL,
            name: "Atlanta Luxury Sprinters, Georgia Private Jet Airport Transportation",
            image: heroImage,
            description: PAGE_DESC,
            serviceType: "Private Jet & FBO Ground Transportation",
            areaServed: [
              { type: "City", name: "Atlanta" },
              { type: "City", name: "Savannah" },
              { type: "City", name: "Augusta" },
              { type: "City", name: "Athens" },
              { type: "Airport", name: "Hartsfield-Jackson Atlanta International Airport (ATL)" },
              { type: "Airport", name: "DeKalb-Peachtree Airport (PDK)" },
              { type: "Airport", name: "Fulton County Airport (FTY)" },
              { type: "Airport", name: "Cobb County International Airport (RYY)" },
              { type: "Airport", name: "Gwinnett County Briscoe Field (LZU)" },
              { type: "Airport", name: "Savannah/Hilton Head International Airport (SAV)" },
              { type: "Airport", name: "Augusta Regional Airport (AGS)" },
              { type: "Airport", name: "Athens Ben Epps Airport (AHN)" },
              { type: "Airport", name: "Brunswick Golden Isles Airport (BQK)" },
            ],
          })
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(buildFaqSchema(faqs)),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SeoLandingPage
      eyebrow="Georgia • Private Aviation Ground Transportation"
      h1="Private Jet Airport Transportation Across Georgia"
      intro="Your flight may land beyond Atlanta. My chauffeurs serve Georgia's private jet airports and FBOs, including PDK, FTY, RYY, LZU, AHN, AGS, SAV and SSI. Choose a Suburban SUV or Mercedes-Benz Sprinter Limousine."
      heroImage={heroImage}
      heroVideo={heroVideo}
      heroAlt="Executive Mercedes-Benz Sprinter interior with captain's chairs for Georgia private jet transfers"
      midImage={midImage}
      midAlt="Interior of a Mercedes-Benz Sprinter Limousine configured for VIP private jet transfers in Georgia"
      sectionOneTitle="From Your Georgia FBO to Your Door"
      sectionOneBody={[
        "Georgia's private airports serve Fortune 500 travel, Masters week, Sea Island vacations and UGA game weekends. We handle the drive from your FBO to the hotel, home or boardroom.",
        "My chauffeurs serve Signature Flight Support, Atlantic Aviation, Million Air, Epps Aviation, Fulton Jet Center, Hill Aircraft, Augusta Aviation, Golden Isles Aviation and more. Planeside access depends on each FBO's authorization.",
        "We track your tail number, coordinate with your crew and handle multi-aircraft groups and multi-stop itineraries. Text me your plans and I'll help choose the vehicle.",
      ]}
      bulletsTitle="Built for High-End Private Aviation Clients"
      bulletsIntro="Your flight department, FBO and chauffeur working from one itinerary."
      bullets={[
        "Planeside pickup at authorized FBOs across Georgia",
        "NDA-bound chauffeurs for celebrity and executive travel",
        "Unbranded vehicles with privacy glass and quiet cabins",
        "Live tail-number tracking with automatic schedule adjustment",
        "Direct coordination with flight departments and security details",
        "Multi-vehicle convoys for touring parties and executive teams",
        "24/7 support including same-day and diverted arrivals",
        "Statewide repositioning from Atlanta to Savannah, Augusta, and the coast",
      ]}
      coverageTitle="Private Jet Airports & FBOs We Serve in Georgia"
      coverageIntro="Metro Atlanta daily, statewide on request, every field below is served by our chauffeur network."
      coverage={[
        "Hartsfield-Jackson (ATL) FBOs",
        "DeKalb-Peachtree (PDK)",
        "Fulton County / Charlie Brown (FTY)",
        "Cobb County McCollum (RYY)",
        "Gwinnett Briscoe Field (LZU)",
        "Cherokee County (CNI)",
        "Falcon Field, Peachtree City (FFC)",
        "Newnan-Coweta (CCO)",
        "Cartersville (VPC)",
        "Savannah/Hilton Head (SAV)",
        "Augusta Regional (AGS)",
        "Daniel Field, Augusta (DNL)",
        "Athens Ben Epps (AHN)",
        "Gainesville Lee Gilmer (GVL)",
        "Macon Middle Georgia (MCN)",
        "Columbus (CSG)",
        "LaGrange-Callaway (LGC)",
        "Albany (ABY)",
        "Valdosta Regional (VLD)",
        "Brunswick Golden Isles (BQK)",
        "St. Simons Island (SSI)",
        "Rome Russell Field (RMG)",
        "Dalton Municipal (DNN)",
        "Thomasville Regional (TVI)",
      ]}
      routeTable={routeTable}
      detailSections={detailSections}
      faqs={faqs}
      related={[
        {
          to: "/private-jet-car-service-atlanta",
          title: "Private Jet Car Service Atlanta",
          desc: "Our flagship private aviation chauffeur service in metro Atlanta.",
        },
        {
          to: "/fbo-transportation-atlanta",
          title: "FBO Transportation Atlanta",
          desc: "Planeside ground transportation at every Atlanta FBO.",
        },
        {
          to: "/atlanta-private-airport-transfer",
          title: "Atlanta Private Airport Transfer",
          desc: "Transfers from PDK, FTY, RYY, and LZU to any destination.",
        },
        {
          to: "/luxury-car-service-atlanta",
          title: "Luxury Car Service Atlanta",
          desc: "Discreet private car service for celebrities, executives, and private flyers.",
        },
      ]}
      ctaTitle="Text Me Your Georgia Arrival"
      ctaBody="Send your tail number, FBO, arrival time, destination, passengers and bags. I'll check availability myself and send your quote in writing."
    />
  );
}
